"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Sparkles, UserCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const keyPillars = [{
  title: "Senior strategic ownership",
  description: "Work with an experienced consultant who takes full personal responsibility for your search growth."
}, {
  title: "Tailored search strategy",
  description: "Technical, content, and local audits mapped precisely to your business revenue goals."
}, {
  title: "Clear, transparent reporting",
  description: "Honest measurement and visible monthly deliverables without vague agency jargon."
}];
export default function HireConsultant() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="herohireseoconsultant" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28 text-foreground border-b border-border">
      {/* Subtle decorative background glow */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 h-96 w-96 rounded-full bg-secondary/40 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -left-24 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8 xl:gap-12">
          {/* Main Hero Copy Column */}
          <div className="lg:col-span-7">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="inline-flex items-center gap-2 mb-6">
              <Badge variant="outline" className="rounded-full px-3.5 py-1 text-xs sm:text-sm font-medium border-border bg-card text-foreground">
                <Sparkles className="size-3.5 text-primary mr-1 inline" />
                Independent SEO Consultant &bull; Working With Businesses Worldwide
              </Badge>
            </motion.div>

            <motion.h1 initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease: "easeOut"
          }} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl leading-[1.15]">
              Hire an SEO Consultant for Your Business
            </motion.h1>

            <motion.p initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2,
            ease: "easeOut"
          }} className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans">
              As an independent SEO consultant, I partner with ambitious businesses worldwide to build dependable search visibility, qualified traffic, and sustainable revenue. Hiring me gives you dedicated senior-level expertise, clear commercial alignment, and proactive execution tailored to your growth goals.
            </motion.p>

            {/* CTAs */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3,
            ease: "easeOut"
          }} className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <Button asChild size="lg" className="rounded-md font-medium shadow-xs">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md font-medium border-border bg-background hover:bg-card text-foreground">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Get a Free SEO Audit</span>
                </Link>
              </Button>
            </motion.div>

            {/* Quick Context Highlights */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4,
            ease: "easeOut"
          }} className="mt-10 pt-8 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
              {keyPillars.map((pillar, i) => <div key={pillar.title} data-index={i} className="space-y-1">
                  <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>{pillar.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {pillar.description}
                  </p>
                </div>)}
            </motion.div>
          </div>

          {/* Visual Column / Portrait Card */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.25,
            ease: "easeOut"
          }} className="w-full max-w-md">
              <div className="relative rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-lg">
                <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-muted">
                  <img src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" data-wvc-srcset="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png-wvc-srcset" data-wvc-sizes="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png-wvc-sizes" alt="Yassine Essaadi, independent freelance SEO consultant" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Overlay Identity Badge */}
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-lg font-bold font-default leading-tight">
                      Yassine Essaadi
                    </p>
                    <p className="text-xs text-white/90 font-sans mt-0.5">
                      Founder &amp; Principal SEO Consultant &bull; YE Consultancy
                    </p>
                  </div>
                </div>

                {/* Direct Consultant Promise Card */}
                <div className="mt-4 rounded-lg bg-background p-4 border border-border">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-secondary text-foreground shrink-0">
                      <UserCheck className="size-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Consultant Promise
                        </span>
                        <ShieldCheck className="size-3.5 text-primary inline" />
                      </div>
                      <p className="text-sm font-medium text-foreground mt-0.5">
                        Dedicated Strategy &amp; Technical Execution
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Practical SEO audits, technical fixes, and bespoke growth strategies crafted specifically for your market.
                      </p>
                    </div>
                  </div>

                  <div className="mt-3.5 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                    <Link to="/FreelanceSeoConsultantPage" className="hover:text-primary transition-colors font-medium underline-offset-2 hover:underline">
                      Freelance consultant model
                    </Link>
                    <span className="text-border">&bull;</span>
                    <Link to="/TechnicalSeoPage" className="hover:text-primary transition-colors font-medium underline-offset-2 hover:underline">
                      Technical SEO
                    </Link>
                    <span className="text-border">&bull;</span>
                    <Link to="/SeoAuditsPage" className="hover:text-primary transition-colors font-medium underline-offset-2 hover:underline">
                      SEO Audits
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}